import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, merge, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { LinkField } from '../../../enums/link-field';
import { AuthKeys } from '../../../enums/local-storage-keys.enum';
import { Operations, OperationsContract } from '../../../models/auth.model';
import { GetUrlRequest } from '../../../models/get-url-request';
import { GetUrlResponse } from '../../../models/geturl-response';
import { LoginSession } from '../../../models/login-session.model';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService, NotificationService } from '../../../services';
import { AppMediatorService } from '../../../services/app-mediator.service';
import { FeatureToggleService } from '../../../services/feature-toggle.service';
import {
    cancelLoadDocuments,
    downloadDocument,
    loadDocument,
    loadDocumentError,
    loadDocumentFinished,
    loadEsignAndOpen,
    loadEsignAndOpenFinished,
    loadLinkAndOpen,
    loadLinkAndOpenFinished,
    openOrDownLoadDocument
} from './document.actions';
import { DocumentData } from './document.reducers';
import { AppFacadeService } from '../../../services/app-facade.service';
import { ProductDocumentType } from '../../../enums/product-document-type.enum';
import { DialogContentType } from '../../../enums/dialog-type.enum';
import { ContractService } from '../../../services/contract.service';
import { PathRoutingService } from '../../../services/path-routing.service';
import { ComponentsToLoad } from '../../../enums/components-to-load.enum';

@Injectable()
export class DocumentEffects {
    private readonly actions$ = inject(Actions);
    private readonly appFacadeService = inject(AppFacadeService);
    private readonly logger = inject(LoggerService);
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly appMediatorService = inject(AppMediatorService);
    private readonly contractService = inject(ContractService);
    private readonly notificationService = inject(NotificationService);
    private readonly translateService = inject(TranslateService);

    loadDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadDocument),
            exhaustMap(({ contractdetails }) => {
                let documentTypes = ['SafetyInstruction'];

                if (this.featureToggleService.isAvailable('displayServiceCertificate')) {
                    documentTypes.push('ServiceCertificate');
                }
                if (this.featureToggleService.isAvailable('displayInspectionDocument')) {
                    documentTypes.push('InspectionCertificate');
                }

                // Syrinx Stock QuickLink specific documents. Prepend Stock for every document
                if (contractdetails.contractId?.trim().length === 0) {
                    documentTypes = [...documentTypes.map(docType => `Stock${docType}`)];
                }

                // Trigger downloadDocument actions concurrently
                return from(documentTypes).pipe(map(documentType => downloadDocument({ contractdetails, documentType })));
            })
        )
    );
    downloadDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(downloadDocument),
            mergeMap(({ contractdetails, documentType }) => {
                let documentData: DocumentData = {
                    docType: documentType,
                    url: '',
                    contractId: contractdetails.contractId,
                    orgType: contractdetails.orgType,
                    isURLFound: false,
                    isLoading: true
                };

                return this.appFacadeService.pdf
                    .downloadPdf(
                        contractdetails.customerId,
                        contractdetails.contractId === '' ? '0' : contractdetails.contractId,
                        documentType,
                        contractdetails.documentValue,
                        contractdetails.orgId,
                        contractdetails.orgType
                    )
                    .pipe(
                        takeUntil(this.actions$.pipe(ofType(cancelLoadDocuments))),
                        map(data => {
                            const url = data?.URL || data?.SecureURL || '';
                            documentData = { ...documentData, url, isURLFound: !!url, isLoading: false, urlExpires: data?.URLExpires, createdDateTime: data?.CreatedDateTime };
                            return loadDocumentFinished({ documentData });
                        }),
                        catchError(error => {
                            documentData = { ...documentData, contractId: '', url: '', isURLFound: false, docType: documentType, isLoading: false };
                            this.logger.logError(error, '', true);
                            return of(loadDocumentError({ documentData, error: error?.message }));
                        })
                    );
            })
        )
    );

    loadLinkAndOpen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadLinkAndOpen),
            switchMap(() => {
                const isSecurePdf = this.getSecureUrlRequest(LinkField.ContractPdfUrl);

                if (!isSecurePdf) {
                    return of(loadLinkAndOpenFinished());
                }

                return this.appFacadeService.pdf.getUrl(isSecurePdf as GetUrlRequest).pipe(
                    map((data: GetUrlResponse) => {
                        if (data.SecureURL !== 'fakeUrl') {
                            this.appFacadeService.pdf.openPdfinNewTab(data.SecureURL);
                        }
                        return loadLinkAndOpenFinished();
                    })
                );
            })
        )
    );

    // Post-login resume of startEsign: re-runs Create from the dedicated marker (the `operations` mechanism
    // is wiped by the post-login user re-persist).
    loadEsignAndOpen$ = createEffect(() =>
        this.actions$.pipe(
            ofType(loadEsignAndOpen),
            switchMap(() => {
                const storage = this.appMediatorService.localStorageService;
                const marker = storage.getEsignResumeMarker();
                if (!marker?.contractId) {
                    return of(loadEsignAndOpenFinished());
                }

                // A QuickLink (non-consumer) session reaches this on any processRoute, including a QuickLink
                // reload that never logged in; running Create there redirects to the signing provider with no
                // login. Defer the resume to a real login and keep the marker so the post-login route resumes.
                if (this.appFacadeService.versionToggleService.isQLButNotConsumerQL()) {
                    return of(loadEsignAndOpenFinished());
                }

                let activeTab: ComponentsToLoad | undefined;
                this.appFacadeService
                    .getActiveTab()
                    .pipe(take(1))
                    .subscribe(tab => (activeTab = tab));
                const redirectUrl = PathRoutingService.buildEsignRedirectUrl(marker.contractId, activeTab);

                this.appFacadeService.setLoading(true);
                return this.contractService.requestEsignUpdate(marker.customerId, marker.contractId, redirectUrl, 'Create').pipe(
                    map(response => {
                        storage.clearEsignResumeMarker();
                        this.appFacadeService.setLoading(false);
                        if (response?.esignUrl && PathRoutingService.isSafeEsignUrl(response.esignUrl)) {
                            window.location.href = response.esignUrl;
                        } else {
                            // esign-request is exempt from the interceptor's 502/504 dialog and the resumed
                            // user lands on the portal, not the button view — surface the failure ourselves.
                            this.logger.logError('eSign resume: Create returned no safe esignUrl', '', true);
                            this.notificationService.error(this.translateService.instant('ESignRequestFailed'));
                        }
                        return loadEsignAndOpenFinished();
                    }),
                    catchError(error => {
                        storage.clearEsignResumeMarker();
                        this.appFacadeService.setLoading(false);
                        this.logger.logError(error, '', true);
                        this.notificationService.error(this.translateService.instant(error?.status === 504 ? 'ESignRequestRetry' : 'ESignRequestFailed'));
                        return of(loadEsignAndOpenFinished());
                    })
                );
            })
        )
    );

    openOrDownLoadInvoiceDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(openOrDownLoadDocument),
            switchMap(({ id, contractdetails }) =>
                this.appFacadeService.getDocumentDataById(ProductDocumentType.Invoice, id).pipe(
                    switchMap(doc => {
                        let isExpired = false;
                        if (doc?.createdDateTime && doc?.urlExpires) {
                            const createdAt = new Date(doc.createdDateTime).getTime();
                            const expiresInMs = (doc.urlExpires || 0) * 1000;
                            const now = Date.now();

                            isExpired = now > createdAt + expiresInMs;
                        }

                        if (!isExpired && doc?.isURLFound) {
                            this.appFacadeService.pdf.openPdfinNewTab(doc.url);
                            this.appFacadeService.setLoading(false);
                            return of();
                        }
                        this.appFacadeService.setLoading(true);

                        return merge(
                            this.actions$.pipe(
                                ofType(loadDocumentFinished),
                                filter(res => res.documentData.docType === ProductDocumentType.Invoice && res.documentData.contractId === contractdetails.contractId && res.documentData.isURLFound),
                                take(1),
                                tap(res => {
                                    this.appFacadeService.setLoading(false);
                                    this.appFacadeService.pdf.openPdfinNewTab(res.documentData.url);
                                })
                            ),
                            this.actions$.pipe(
                                ofType(loadDocumentFinished),
                                filter(res => res.documentData.docType === ProductDocumentType.Invoice && res.documentData.contractId === contractdetails.contractId && !res.documentData.isURLFound),
                                take(1),
                                tap(() => {
                                    this.appFacadeService.setLoading(false);
                                    this.appFacadeService.openDialog(DialogContentType.Failed);
                                })
                            ),
                            this.actions$.pipe(
                                ofType(loadDocumentError),
                                tap(() => {
                                    this.appFacadeService.setLoading(false);
                                    this.appFacadeService.openDialog(DialogContentType.Failed);
                                }),
                                take(1)
                            )
                        ).pipe(startWith(downloadDocument({ contractdetails, documentType: ProductDocumentType.Invoice })));
                    })
                )
            )
        )
    );

    private getSecureUrlRequest(linkField: LinkField): GetUrlRequest | false {
        const operations = this.appMediatorService.localStorageService.getSessionStorageItem<Operations & LoginSession>(AuthKeys.User);
        if (operations !== undefined && operations?.operations?.contract?.[linkField as keyof OperationsContract] !== undefined) {
            const urlRequest: GetUrlRequest = {
                customerId: operations?.customerId ?? '',
                contractId: operations?.contractId ?? '',
                linkField: linkField
            };

            return urlRequest;
        }
        return false;
    }
}
